/**
 * Hub / App Store — VTEX / Shopify / Uappi (DOC 11).
 * JWT do painel — webhook_secret plano só em connect/rotate (quando aplicável).
 */

import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";

const apiBase = config.api.API_URL;

export type IntegrationProvider = {
  slug: string;
  name: string;
  category: string;
  capabilities: string[];
  config_schema?: Record<string, unknown> | null;
  sort_order: number;
};

export type TenantIntegrationLastSync = {
  imported: number;
  updated: number;
  skipped: number;
  finished_at: string | null;
  started_at?: string | null;
  duration_seconds?: number | null;
  mode?: "incremental" | "full" | null;
  progress_percent?: number;
  phase?: string | null;
  orders_processed?: number;
  carts_converted?: number;
  emails_cancelled?: number;
};

export type TenantIntegration = {
  id: number;
  provider_slug: string;
  capability: string;
  account_name: string | null;
  app_key_masked: string | null;
  enabled: boolean;
  public_key: string;
  webhook_url: string;
  last_synced_at: string | null;
  last_webhook_at?: string | null;
  last_error: string | null;
  events_today?: number;
  events_total?: number;
  last_skip_reason?: string | null;
  automation_enabled?: boolean;
  abandoned_cart_tag_id?: number | null;
  customer_tag_id?: number | null;
  customer_count?: number;
  vtex_customer_tag_id?: number | null;
  vtex_customer_count?: number;
  last_sync?: TenantIntegrationLastSync | null;
  sync_cooldown_minutes?: number;
  /** @deprecated use sync_cooldown_minutes */
  vtex_sync_cooldown_minutes?: number;
  created_at?: string;
  updated_at?: string;
  /** Só após connect/rotate. */
  webhook_secret?: string;
};

export type ConnectVtexPayload = {
  account_name: string;
  app_key: string;
  app_token: string;
};

export type UpdateVtexPayload = {
  account_name?: string;
  app_key?: string;
  app_token?: string;
  enabled?: boolean;
  rotate_webhook_secret?: boolean;
};

export type ConnectShopifyPayload = {
  shop: string;
  access_token: string;
  api_secret: string;
};

export type UpdateShopifyPayload = {
  shop?: string;
  access_token?: string;
  api_secret?: string;
  enabled?: boolean;
};

export type ConnectUappiPayload = {
  store_base_url: string;
  api_token: string;
};

export type UpdateUappiPayload = {
  store_base_url?: string;
  api_token?: string;
  enabled?: boolean;
  rotate_webhook_secret?: boolean;
};

export const getIntegrationCatalog = (): Promise<
  ApiSuccess<IntegrationProvider[]>
> => axios.get(apiBase + "/integrations/catalog");

export const getInstalledIntegrations = (): Promise<
  ApiSuccess<TenantIntegration[]>
> => axios.get(apiBase + "/integrations");

export const getVtexIntegration = (): Promise<
  ApiSuccess<TenantIntegration>
> => axios.get(apiBase + "/integrations/vtex");

export const connectVtex = (
  payload: ConnectVtexPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.post(apiBase + "/integrations/vtex", payload);

export const updateVtex = (
  payload: UpdateVtexPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.patch(apiBase + "/integrations/vtex", payload);

export const disconnectVtex = (): Promise<ApiSuccess<null>> =>
  axios.delete(apiBase + "/integrations/vtex");

export const testVtexConnection = (): Promise<
  ApiSuccess<{ ok: boolean; message?: string }>
> => axios.post(apiBase + "/integrations/vtex/test");

export const syncVtexContacts = (payload?: {
  force_full?: boolean;
}): Promise<
  ApiSuccess<{ queued: boolean; last_synced_at: string | null }>
> => axios.post(apiBase + "/integrations/vtex/sync", payload ?? {});

export const getShopifyIntegration = (): Promise<
  ApiSuccess<TenantIntegration>
> => axios.get(apiBase + "/integrations/shopify");

export const connectShopify = (
  payload: ConnectShopifyPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.post(apiBase + "/integrations/shopify", payload);

export const updateShopify = (
  payload: UpdateShopifyPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.patch(apiBase + "/integrations/shopify", payload);

export const disconnectShopify = (): Promise<ApiSuccess<null>> =>
  axios.delete(apiBase + "/integrations/shopify");

export const testShopifyConnection = (): Promise<
  ApiSuccess<{ ok: boolean; message?: string }>
> => axios.post(apiBase + "/integrations/shopify/test");

export const syncShopifyContacts = (payload?: {
  force_full?: boolean;
}): Promise<
  ApiSuccess<{ queued: boolean; last_synced_at: string | null }>
> => axios.post(apiBase + "/integrations/shopify/sync", payload ?? {});

export const getUappiIntegration = (): Promise<
  ApiSuccess<TenantIntegration>
> => axios.get(apiBase + "/integrations/uappi");

export const connectUappi = (
  payload: ConnectUappiPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.post(apiBase + "/integrations/uappi", payload);

export const updateUappi = (
  payload: UpdateUappiPayload
): Promise<ApiSuccess<TenantIntegration>> =>
  axios.patch(apiBase + "/integrations/uappi", payload);

export const disconnectUappi = (): Promise<ApiSuccess<null>> =>
  axios.delete(apiBase + "/integrations/uappi");

export const testUappiConnection = (): Promise<
  ApiSuccess<{ ok: boolean; message?: string }>
> => axios.post(apiBase + "/integrations/uappi/test");

export const syncUappiContacts = (payload?: {
  force_full?: boolean;
}): Promise<
  ApiSuccess<{ queued: boolean; last_synced_at: string | null }>
> => axios.post(apiBase + "/integrations/uappi/sync", payload ?? {});

export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    message?: string;
    response?: { data?: { error?: { message?: string }; message?: string } };
  };
  return (
    e?.response?.data?.error?.message ||
    e?.response?.data?.message ||
    e?.message ||
    fallback
  );
}
